import { useState } from "react";
import { useToaster } from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import { useWriteAPI } from "@dealer-center/ddc-pages-incentives-manager-fetch";
import type {
  IncentivesRecord,
  RawIncentivesState
} from "@dealer-center/ddc-pages-incentives-manager-interface";
import { splitByExpiration } from "../utils/utils";

interface UseIncentiveActionsParams {
  ccUserInfo: {
    accountId: string;
    userId: string;
  };
  currentTab: string;
  dispatch: (action: any) => void;
  setIsChanging: (isChanging: boolean) => void;
  setIsDeleting: (isDeleting: boolean) => void;
  setModalButton: (button: any) => void;
  incentives: IncentivesRecord[] | undefined;
}

export const useIncentiveActions = ({
  ccUserInfo,
  currentTab,
  dispatch,
  setIsChanging,
  setIsDeleting,
  setModalButton,
  incentives
}: UseIncentiveActionsParams) => {
  const { toastError } = useToaster();

  const [changes, setChanges] = useState<{
    toState: string;
    fromState: string;
    incentivesIds: string[];
  }>({
    toState: "",
    fromState: "",
    incentivesIds: []
  });

  const changeStatusMutation = useWriteAPI({
    resource: "update-incentives-status",
    asAccount: ccUserInfo.accountId,
    asUser: ccUserInfo.userId,
    locale: Locale.getCurrent().replace("-", "_"),
    afterSuccess: (body: RawIncentivesState[]) => {
      setIsChanging(false);
      setModalButton(null);
      body.forEach(block => {
        // "Reject" returns a logicalState of "NONE", so accountMap.status needs to be used
        // Group incentives by their actual status to handle potential mixed statuses
        if (block.logicalState === "NONE" && block.incentives.length > 0) {
          const incentivesByStatus = new Map<string, string[]>();

          block.incentives.forEach(incentive => {
            const status = incentive.accountMap.status || "NONE";
            const incentiveId = incentive.accountMap.incentiveId || "";

            if (!incentivesByStatus.has(status)) {
              incentivesByStatus.set(status, []);
            }
            incentivesByStatus.get(status)?.push(incentiveId);
          });

          incentivesByStatus.forEach((incentiveIds, status) => {
            let toState = status;
            if (toState === "REJECTED") {
              toState = "DECLINED";
            }
            // Map COMP_APPROVED and COMP_REJECTED to NONE - removes incentive from parent account view
            // Backend tracks the incentive correctly, but parent accounts don't have
            // an upstream compliance tab, so the incentive shouldn't appear anywhere
            if (toState === "COMP_APPROVED" || toState === "COMP_REJECTED") {
              toState = "NONE";
            }

            dispatch({
              type: "MOVE_INCENTIVES",
              incentiveIds: incentiveIds,
              fromState: changes.fromState,
              toState: toState
            });
          });
        } else {
          const incentiveIds = block.incentives.map(
            incentive => incentive.accountMap.incentiveId
          );

          let toState = block.logicalState;
          if (toState === "REJECTED") {
            toState = "DECLINED";
          }

          dispatch({
            type: "MOVE_INCENTIVES",
            incentiveIds: incentiveIds,
            fromState: changes.fromState,
            toState: toState
          });
        }
      });
    }
  });

  const deleteMutation = useWriteAPI({
    resource: "delete-incentives",
    asAccount: ccUserInfo.accountId,
    asUser: ccUserInfo.userId,
    locale: Locale.getCurrent().replace("-", "_"),
    afterSuccess: body => {
      setIsDeleting(false);
      setModalButton(null);
      dispatch({
        type: "DELETE_INCENTIVES",
        incentiveIds: changes.incentivesIds,
        fromState: changes.fromState,
        accountId: ccUserInfo.accountId
      });
    }
  });

  const onChangeStatus = async (incentiveIds: string[], status: string) => {
    setChanges({
      toState: status === "REJECTED" ? "DECLINED" : status,
      fromState: currentTab,
      incentivesIds: incentiveIds
    });

    try {
      await changeStatusMutation.mutateAsync({
        body: incentiveIds,
        extraURLParams: `status=${status}`
      });
    } catch (error) {
      toastError(Locale.getText("CANNOT") as string);
      console.warn(error.message); // eslint-disable-line no-console
      setIsChanging(false);
      setModalButton(null);
    }
  };

  const onDeleteStatus = async (incentiveIds: string[]) => {
    setChanges({
      toState: "DELETE_INCENTIVES",
      fromState: currentTab,
      incentivesIds: incentiveIds
    });
    try {
      await deleteMutation.mutateAsync({
        body: incentiveIds
      });
    } catch (error) {
      toastError(Locale.getText("CANNOT") as string);
      console.warn(error.message); // eslint-disable-line no-console
      setIsDeleting(false);
      setModalButton(null);
    }
  };

  const onPublish = async (ids: string[]) => {
    if (!incentives) return;
    const { expired, notExpired } = splitByExpiration(ids, incentives);
    if (expired.length > 0) {
      await onChangeStatus(expired, "EXPIRED");
    }
    if (notExpired.length > 0) {
      await onChangeStatus(notExpired, "PUBLISHED");
    }
  };

  const onApprove = async (ids: string[]) => {
    if (!incentives) return;
    const { expired, notExpired } = splitByExpiration(ids, incentives);
    const complianceApproved: string[] = [];
    const published: string[] = [];
    notExpired.forEach(id => {
      const record = incentives.find(
        incentive => incentive.content.incentiveId === id
      );
      if (record?.accountMap.status === "ACTREQ_COMP") {
        complianceApproved.push(id);
      } else {
        published.push(id);
      }
    });
    if (expired.length > 0) {
      await onChangeStatus(expired, "EXPIRED");
    }
    if (complianceApproved.length > 0) {
      await onChangeStatus(complianceApproved, "COMP_APPROVED");
    }
    if (published.length > 0) {
      await onChangeStatus(published, "PUBLISHED");
    }
  };

  return {
    onChangeStatus,
    onDeleteStatus,
    onPublish,
    onApprove
  };
};
